import axios from 'axios'
import api from './axiosApi'

// Types from OpenAPI spec
export interface FileUploadUrlRequest {
  original_filename: string
  content_type: string
}

export interface FileUploadUrlResponse {
  file_id: string
  upload_url: string
  blob_name: string
  expires_at: string
}

export interface FileConfirmRequest {
  size_bytes?: number | null
}

export interface FileRead {
  id: string
  original_filename: string
  content_type: string
  size_bytes: number | null
  blob_name: string
  container_name: string
  status: 'PENDING' | 'CONFIRMED' | 'DELETED'
  created_by: string
  updated_by: string
  created_at: string
  updated_at: string
  confirmed_at: string | null
}

export interface FileUploadResult {
  fileId: string
  blobName: string
  sizeBytes: number
  fileName: string
}

/**
 * Step 1: Get a presigned URL for uploading a file
 * POST /files/upload-url
 */
export async function getPresignedUrl(
  filename: string,
  contentType: string
): Promise<FileUploadUrlResponse> {
  const response = await api.post<FileUploadUrlResponse>('/files/upload-url', {
    original_filename: filename,
    content_type: contentType,
  } satisfies FileUploadUrlRequest)
  return response.data
}

/**
 * Step 2: Upload file to the presigned URL
 * Uses raw axios (not authenticated) since it's a direct blob storage upload
 */
export async function uploadToPresignedUrl(
  uploadUrl: string,
  file: File
): Promise<void> {
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type,
      'x-ms-blob-type': 'BlockBlob',
    },
  })
}

/**
 * Step 3: Confirm the file upload is complete
 * POST /files/{file_id}/confirmations
 */
export async function confirmFileUpload(
  fileId: string,
  sizeBytes?: number
): Promise<FileRead> {
  const response = await api.post<FileRead>(`/files/${fileId}/confirmations`, {
    size_bytes: sizeBytes ?? null,
  } satisfies FileConfirmRequest)
  return response.data
}

/**
 * Orchestrates the complete file upload flow:
 * 1. Get presigned URL
 * 2. Upload to presigned URL
 * 3. Confirm upload
 *
 * @param file - The file to upload
 * @returns File metadata including fileId for creating product lists
 */
export async function uploadFile(file: File): Promise<FileUploadResult> {
  // Step 1: Get presigned URL
  const { file_id, upload_url, blob_name } = await getPresignedUrl(
    file.name,
    file.type || 'application/octet-stream'
  )

  // Step 2: Upload to presigned URL
  await uploadToPresignedUrl(upload_url, file)

  // Step 3: Confirm upload
  await confirmFileUpload(file_id, file.size)

  return {
    fileId: file_id,
    blobName: blob_name,
    sizeBytes: file.size,
    fileName: file.name,
  }
}
